import type { FlockEconomics } from "@/types/models";

// Cost-driver breakdown for the flock card. Splits a flock's cost into
// feed / chick / overhead so the owner sees WHAT drives cost — as a proportion
// bar plus per-driver rows (per-egg ₹ or whole ₹). Pure — all inputs come from
// the FlockEconomics payload; no backend call.
//
// Scope on purpose: this panel shows ONLY the cost decomposition. The authoritative
// totals — cost/egg, realized price, margin/egg (per-egg) and operating cost
// (absolute) — already live on the card header and the metric grid, taken straight
// from the payload. The panel deliberately does NOT restate them, so there is no
// total for the rounded rows to "fail to foot" against and nothing to drift from
// the header. Each driver row is its own true value.
// See docs/architecture/cost-driver-waterfall.md.

export type WaterfallMode = "per-egg" | "absolute";

export interface WaterfallDriver {
  key: "feed" | "chick" | "overhead";
  label: string;
  value: number; // per-egg ₹ (per-egg mode) or whole ₹ (absolute mode)
  sharePct: number; // 0–100, this driver's share of the shown cost; shares sum to 100
}

export interface Waterfall {
  mode: WaterfallMode;
  // Fixed order feed → chick → overhead; only strictly-positive drivers are kept
  // (a zero/near-zero component is dropped, and a negative one — e.g. an expense
  // refund netting overhead below zero — has no place in a proportion bar; the
  // header's net cost/egg still reflects any such credit).
  drivers: WaterfallDriver[];
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

const DRIVER_LABELS: Record<WaterfallDriver["key"], string> = {
  feed: "Feed",
  chick: "Chick",
  overhead: "Overhead",
};

// Integer percentages of a set of amounts that always sum to 100 (largest-
// remainder / Hamilton method), so the displayed shares never read as 99% or 101%.
function percentShares(amounts: number[]): number[] {
  const total = amounts.reduce((s, a) => s + a, 0);
  if (total <= 0) return amounts.map(() => 0);
  const exact = amounts.map((a) => (a / total) * 100);
  const floor = exact.map((e) => Math.floor(e));
  let remaining = 100 - floor.reduce((s, f) => s + f, 0);
  const order = exact
    .map((e, i) => ({ i, frac: e - Math.floor(e) }))
    .sort((a, b) => b.frac - a.frac);
  const shares = [...floor];
  for (const { i } of order) {
    if (remaining <= 0) break;
    shares[i] += 1;
    remaining -= 1;
  }
  // Every shown driver is a strictly-positive cost, so none should read "0% of
  // cost". Bump any zero to 1%, taking the point from the largest share so the
  // total stays 100. (At most 3 drivers, so the largest always has room.)
  for (let i = 0; i < shares.length; i++) {
    if (shares[i] === 0) {
      let mi = 0;
      for (let j = 1; j < shares.length; j++) if (shares[j] > shares[mi]) mi = j;
      if (shares[mi] > 1) {
        shares[mi] -= 1;
        shares[i] = 1;
      }
    }
  }
  return shares;
}

// Per-egg mode needs eggs (a divisor), a realized price, and an authoritative
// cost_per_egg (all header preconditions). Without them we fall back to an
// absolute-₹ cost buildup so a pre-lay / not-yet-sold flock still shows where
// money is going.
export function buildWaterfall(econ: FlockEconomics): Waterfall {
  const perEgg = econ.eggs_produced > 0 && econ.egg_price_realized !== null && econ.cost_per_egg !== null;
  const divisor = perEgg ? econ.eggs_produced : 1;

  // True per-driver value, at display precision; keep only what the bar can show
  // (strictly positive after rounding — drops zero/near-zero and negative credits).
  const computed = (
    [
      { key: "feed", amount: econ.cost.feed },
      { key: "chick", amount: econ.cost.chick },
      { key: "overhead", amount: econ.cost.overhead },
    ] as const
  )
    .map((d) => ({
      key: d.key,
      label: DRIVER_LABELS[d.key],
      value: perEgg ? round2(d.amount / divisor) : Math.round(d.amount),
    }))
    .filter((d) => d.value > 0);

  // Shares are derived from the SHOWN values, so the % and the ₹ on a row can't
  // tell contradictory stories.
  const shares = percentShares(computed.map((d) => d.value));
  const drivers: WaterfallDriver[] = computed.map((d, i) => ({ ...d, sharePct: shares[i] }));

  return {
    mode: perEgg ? "per-egg" : "absolute",
    drivers,
  };
}
